import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { trackCheckoutReturn } from '@/hooks/useActivityTracker';

/**
 * Displays a friendly banner/modal when Stripe redirects back with
 * ?checkout=success or ?checkout=cancel, then clears the query string.
 * Purely presentational — does NOT gate anything.
 */
export const CheckoutReturnNotice: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch } = useSubscription();
  const { user } = useAuth();
  const [notice, setNotice] = useState<null | 'success' | 'cancel'>(null);
  const handledRef = useRef<string | null>(null);
  const trackedRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const state = params.get('checkout');
    if (state !== 'success' && state !== 'cancel') return;

    const key = `${location.pathname}?${state}`;

    // Fire checkout_return exactly once per return, independent of the notice
    // state (which may be dismissed and re-triggered).
    if (trackedRef.current !== key) {
      trackedRef.current = key;
      trackCheckoutReturn(user?.id, state === 'success' ? 'success' : 'cancelled');
    }

    if (handledRef.current === key) return;
    handledRef.current = key;

    setNotice(state);

    // Refetch subscription a few times — webhook is async.
    if (state === 'success') {
      refetch();
      const t1 = setTimeout(() => refetch(), 2500);
      const t2 = setTimeout(() => refetch(), 7000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [location.pathname, location.search, refetch, user?.id]);

  if (!notice) return null;

  const close = () => {
    setNotice(null);
    const params = new URLSearchParams(location.search);
    params.delete('checkout');
    params.delete('session_id');
    const rest = params.toString();
    navigate(
      { pathname: location.pathname, search: rest ? `?${rest}` : '', hash: location.hash },
      { replace: true }
    );
  };

  // Darstellung im Stil der Vorlage 08_profil-einstellungen-pro/pro_preise_*_erfolg (Erfolgskarte, 9.A-42).
  // Logik oben (Tracking, Neuladen, Rückkehr-URL, Schließen) unverändert.
  if (notice === 'success') {
    const checkRow = (label: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: '#c6cfdb', lineHeight: 1.5 }}>
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: 6,
            background: '#1db943',
            color: '#07150b',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 900,
            flex: '0 0 auto',
          }}
        >
          ✓
        </span>
        <span>{label}</span>
      </div>
    );

    return (
      <>
        <style>{`
          @keyframes aimupCheckoutFadeIn {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.97); }
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          @keyframes aimupOverlayFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .aimup-checkout-btn {
            background: #1db943 !important;
            color: #07150b !important;
            transition: background 150ms ease;
          }
          .aimup-checkout-btn:hover { background: #22c94b !important; }
          .aimup-checkout-dialog :is(button):focus-visible { outline: 2px solid #1db943; outline-offset: 2px; }
        `}</style>
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(5,9,15,0.72)',
            zIndex: 1000,
            animation: 'aimupOverlayFadeIn 180ms ease-out',
          }}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="aimup-checkout-title"
          className="aimup-checkout-dialog"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001,
            width: 'min(560px, calc(100vw - 32px))',
            boxSizing: 'border-box',
            background: '#182335',
            borderStyle: 'solid',
            borderWidth: '3px 1px 1px',
            borderColor: '#1db943 rgba(29,185,67,0.45) rgba(29,185,67,0.45)',
            borderRadius: 16,
            padding: 22,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 14,
            flexWrap: 'wrap',
            textAlign: 'left',
            boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
            fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
            lineHeight: 'normal',
            color: '#f4f6f8',
            animation: 'aimupCheckoutFadeIn 200ms ease-out',
          }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Schließen"
            style={{
              position: 'absolute',
              top: 8,
              right: 10,
              background: 'transparent',
              color: '#7f8a9c',
              border: 'none',
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
              padding: '4px 8px',
              minHeight: 0,
            }}
          >
            ×
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 260px', minWidth: 0, paddingRight: 24 }}>
            <h2
              id="aimup-checkout-title"
              style={{ fontSize: 17, fontWeight: 800, margin: 0, color: '#f4f6f8', lineHeight: 1.3 }}
            >
              Willkommen bei AimUp Pro
            </h2>
            <p style={{ margin: 0, color: '#aeb6c3', fontSize: 13, lineHeight: 1.5 }}>
              Danke für dein Abo. Alle Pro-Funktionen sind ab sofort freigeschaltet.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {checkRow('Persönliches Training')}
              {checkRow('AimUp Liga')}
              {checkRow('Jäger & Fuchs')}
            </div>
          </div>

          <button
            type="button"
            onClick={close}
            className="aimup-checkout-btn"
            style={{
              minHeight: 44,
              padding: '0 20px',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 999,
              fontFamily: 'inherit',
              fontWeight: 850,
              fontSize: 14,
            }}
          >
            Los geht's
          </button>
        </div>
      </>
    );
  }

  // Cancel — gleicher Text, Hinweis im Stil der Vorlage
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 200,
        boxSizing: 'border-box',
        width: 'min(520px, calc(100vw - 32px))',
        background: '#182335',
        color: '#c6cfdb',
        border: '1px solid #263449',
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
        fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
      role="status"
    >
      <div style={{ flex: 1 }}>
        Der Bestellvorgang wurde abgebrochen. Es wurde nichts abgebucht.
      </div>
      <button
        type="button"
        onClick={close}
        style={{
          background: 'transparent',
          color: '#7f8a9c',
          border: 'none',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          padding: '2px 6px',
          minHeight: 0,
        }}
        aria-label="Schließen"
      >
        ×
      </button>
    </div>
  );
};
